import React, { memo, useState, useEffect, useCallback, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import { Shape } from '../../types/Shape';
import { Annotation, AnnotationPoint } from '../../types/Annotation';
import HTMLShapeBase from './HTMLShapeBase';
import AnnotationCanvas from '../annotation/AnnotationCanvas';
import AnnotationToolbar from '../annotation/AnnotationToolbar';
import { useAnnotation } from '../../hooks/useAnnotation';
import { pdfjsLib } from '../../utils/pdfConfig';
import { useI18n } from '../../contexts/I18nContext';
import type { PDFDocumentProxy } from 'pdfjs-dist';

interface HTMLPDFProps {
  shape: Shape;
  isSelected: boolean;
  showResizeHandles?: boolean;
  stagePos: { x: number; y: number };
  stageScale: number;
  isDrawing?: boolean;
  isEditing?: boolean;
  currentTool?: string;
  onDragStart?: (shapeId: string) => void;
  onDragMove?: (shapeId: string, newX: number, newY: number) => void;
  onDragEnd?: (shapeId: string) => void;
  onResize?: (shapeId: string, newWidth: number, newHeight: number, newX?: number, newY?: number) => void;
  onResizeEnd?: () => void;
  onClick?: (shapeId: string, e: React.MouseEvent) => void;
  onDoubleClick?: (shapeId: string, e: React.MouseEvent) => void;
  onContextMenu?: (shapeId: string, e: React.MouseEvent) => void;
  onMouseEnter?: (shapeId: string) => void;
  onMouseLeave?: () => void;
  onStageClick?: (canvasX: number, canvasY: number, e: React.MouseEvent) => void;
  onShapeUpdate?: (shapeId: string, updates: Partial<Shape>) => void;
  onExtractPage?: (shapeId: string, pageNum: number) => void;
  onAnnotationUpdate?: (shapeId: string, annotations: Annotation[]) => void;
}

const HTMLPDF: React.FC<HTMLPDFProps> = (props) => {
  const { t } = useI18n();
  const { shape, stageScale, onShapeUpdate, isSelected, onExtractPage, onAnnotationUpdate } = props;
  const [pdfImage, setPdfImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPageNum, setSelectedPageNum] = useState(shape.currentPage || 1);
  const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
  const loadingRef = useRef<boolean>(false);
  const loadedRef = useRef<boolean>(false);
  const lastRenderScaleRef = useRef<number>(0);

  // Annotation 상태
  const [isAnnotationMode, setIsAnnotationMode] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState<{ x: number; y: number } | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentSize, setContentSize] = useState({ width: 0, height: 0 });

  const annotation = useAnnotation({
    initialAnnotations: shape.annotations || [],
    onAnnotationsChange: (annotations) => {
      if (onAnnotationUpdate) {
        onAnnotationUpdate(shape.id, annotations);
      }
    },
  });

  // 외부 annotations 변경 시 동기화
  useEffect(() => {
    if (shape.annotations && shape.annotations !== annotation.state.annotations) {
      annotation.setAnnotations(shape.annotations);
    }
  }, [shape.annotations]);

  // 콘텐츠 크기 추적 (AnnotationCanvas 크기 동기화)
  useEffect(() => {
    if (!contentRef.current) return;
    const updateSize = () => {
      if (contentRef.current) {
        const rect = contentRef.current.getBoundingClientRect();
        setContentSize({ width: rect.width, height: rect.height });
      }
    };
    updateSize();
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(contentRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // 클릭 핸들러 (선택/드래그만)
  const handleClick = useCallback(
    (shapeId: string, e: React.MouseEvent) => {
      if (isAnnotationMode) return;
      if (props.onClick) props.onClick(shapeId, e);
    },
    [isAnnotationMode, props.onClick]
  );

  // 더블클릭 → 어노테이션 모드 진입
  const handleDoubleClick = useCallback(
    (shapeId: string, e: React.MouseEvent) => {
      if (isAnnotationMode) return;
      if (contentRef.current) {
        const rect = contentRef.current.getBoundingClientRect();
        setToolbarPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }
      setIsAnnotationMode(true);
      annotation.setTool('none');
      if (props.onDoubleClick) props.onDoubleClick(shapeId, e);
    },
    [isAnnotationMode, annotation, props.onDoubleClick]
  );

  // 어노테이션 모드 종료
  const handleCloseAnnotation = useCallback(() => {
    setIsAnnotationMode(false);
    setToolbarPosition(null);
    annotation.setTool('none');
  }, [annotation]);

  const handleHighlighterAdd = useCallback(
    (points: AnnotationPoint[]) => annotation.addHighlighter(points),
    [annotation]
  );

  const handleUnderlineAdd = useCallback(
    (startPoint: AnnotationPoint, endPoint: AnnotationPoint) => annotation.addUnderline(startPoint, endPoint),
    [annotation]
  );

  const handleAnnotationDelete = useCallback(
    (id: string) => annotation.deleteAnnotation(id),
    [annotation]
  );

  // ESC / 단축키 / 외부 클릭으로 어노테이션 모드 종료
  useEffect(() => {
    if (!isAnnotationMode) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCloseAnnotation();
      else if (e.key === 'h' || e.key === 'H') annotation.setTool('highlighter');
      else if (e.key === 'u' || e.key === 'U') annotation.setTool('underline');
      else if (e.key === 'e' || e.key === 'E') annotation.setTool('eraser');
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
        handleCloseAnnotation();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAnnotationMode, handleCloseAnnotation, annotation]);

  // Canvas 크기 계산 (border 제외)
  const borderWidth = (shape.strokeWidth || 1) * stageScale;
  const canvasWidth = Math.max(0, contentSize.width - borderWidth * 2);
  const canvasHeight = Math.max(0, contentSize.height - borderWidth * 2);

  // Page Browser State
  const [showBrowser, setShowBrowser] = useState(false);
  const [browserTab, setBrowserTab] = useState<'pages' | 'outline'>('pages');
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set());
  const thumbnailRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const browserContentRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Outline (목차) State
  interface OutlineItem { title: string; pageNum: number; level: number; }
  const [outlineItems, setOutlineItems] = useState<OutlineItem[]>([]);
  const outlineLoadedRef = useRef(false);

  // Intersection Observer for Thumbnails
  useEffect(() => {
    if (!showBrowser) return;

    const observer = new IntersectionObserver(
      (entries) => {
        setVisiblePages(prev => {
          const newSet = new Set(prev);
          entries.forEach(entry => {
            const pageNum = parseInt(entry.target.getAttribute('data-page') || '0');
            if (pageNum > 0) {
              if (entry.isIntersecting) {
                newSet.add(pageNum);
              } else {
                newSet.delete(pageNum);
              }
            }
          });
          return newSet;
        });
      },
      { root: browserContentRef.current, rootMargin: '50px' }
    );
    observerRef.current = observer;

    return () => {
      observer.disconnect();
    };
  }, [showBrowser]);


  const setPageRef = useCallback((el: HTMLDivElement | null, pageNum: number) => {
    if (el && observerRef.current) {
      observerRef.current.observe(el);
    }
    thumbnailRefs.current[pageNum] = el;
  }, []);


  // Toggle browser visibility based on selection or manual toggle
  // Current design: Show browser left of the PDF object when selected, or always if pinned? 
  // User request: "객체 왼쪽에 페이지 브라우저 붙이고" (Attach page browser to the left of the object)
  // Let's render it absolutely positioned to the left of the content.

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy().catch(() => {});
        pdfDocRef.current = null;
      }
    };
  }, [shape.id]);

  // Main PDF Page Rendering
  const loadPage = useCallback(async (pageNum: number, forceRerender = false) => {
    if (loadingRef.current) return;
    if (!shape.pdfFile && !shape._pdfBase64) {
      return;
    }

    // 줌 변경에 의한 재렌더링인지 확인
    const dpr = window.devicePixelRatio || 1;
    const effectiveScale = stageScale * dpr;

    // 이미 충분한 해상도로 렌더링되었으면 스킵
    if (!forceRerender && loadedRef.current && lastRenderScaleRef.current >= effectiveScale * 0.9) {
      return;
    }

    try {
      loadingRef.current = true;
      // 페이지 변경이 아닌 줌 변경일 때는 로딩 표시 안 함
      if (!loadedRef.current) setIsLoading(true);

      let dataUrl: string;

      if (shape.pdfFile) {
        const reader = new FileReader();
        dataUrl = await new Promise<string>((resolve, reject) => {
          reader.onload = (e) => {
            const result = e.target?.result;
            if (typeof result === 'string') {
              resolve(result);
            } else {
              reject(new Error('Failed to read PDF file'));
            }
          };
          reader.onerror = () => reject(new Error('FileReader error'));
          reader.readAsDataURL(shape.pdfFile!);
        });
      } else {
        dataUrl = shape._pdfBase64!;
      }

      const pdf = await pdfjsLib.getDocument({
        url: dataUrl,
        useWorkerFetch: false,
        disableAutoFetch: false,
      }).promise;
      pdfDocRef.current = pdf;

      // 목차 로딩 (최초 1회)
      if (!outlineLoadedRef.current) {
        outlineLoadedRef.current = true;
        pdf.getOutline().then(async (outline) => {
          if (!outline || outline.length === 0) return;
          const items: OutlineItem[] = [];
          const flatten = async (nodes: typeof outline, level: number) => {
            for (const node of nodes) {
              let pageNum = 1;
              try {
                if (node.dest) {
                  const dest = typeof node.dest === 'string'
                    ? await pdf.getDestination(node.dest)
                    : node.dest;
                  if (dest && dest[0]) {
                    const pageIndex = await pdf.getPageIndex(dest[0]);
                    pageNum = pageIndex + 1;
                  }
                }
              } catch { /* dest 변환 실패 시 1페이지 */ }
              items.push({ title: node.title, pageNum, level });
              if (node.items && node.items.length > 0) {
                await flatten(node.items, level + 1);
              }
            }
          };
          await flatten(outline, 0);
          setOutlineItems(items);
        }).catch(() => {});
      }

      const page = await pdf.getPage(pageNum);

      const baseViewport = page.getViewport({ scale: 1.0 });

      // 줌 레벨과 devicePixelRatio를 반영한 렌더 스케일 계산
      const shapeScale = Math.max(
        (shape.width || 600) / baseViewport.width,
        (shape.height || 800) / baseViewport.height,
        1.5
      );
      const renderScale = Math.min(shapeScale * effectiveScale, 8.0);

      const viewport = page.getViewport({ scale: renderScale });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas context not available');

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';

      await page.render({
        canvasContext: context,
        viewport: viewport,
        canvas: canvas,
      }).promise;

      const imageData = canvas.toDataURL();
      setPdfImage(imageData);
      setIsLoading(false);
      loadingRef.current = false;
      loadedRef.current = true;
      lastRenderScaleRef.current = effectiveScale;

      if (!shape.totalPages || shape.totalPages !== pdf.numPages) {
        if (onShapeUpdate) {
          onShapeUpdate(shape.id, { totalPages: pdf.numPages });
        }
      }

    } catch {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [shape.pdfFile, shape._pdfBase64, shape.width, shape.height, shape.id, shape.totalPages, onShapeUpdate, stageScale]);

  // 페이지 변경 시 렌더링
  useEffect(() => {
    if (loadedRef.current) return;
    if (loadingRef.current) return;
    loadPage(selectedPageNum, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPageNum]);

  // 줌 레벨 변경 시 debounce로 재렌더링
  useEffect(() => {
    if (!loadedRef.current) return;
    const timer = setTimeout(() => {
      loadPage(selectedPageNum);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageScale]);

  const handlePrevPage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedPageNum > 1) {
      loadedRef.current = false;
      const newPage = selectedPageNum - 1;
      setSelectedPageNum(newPage);
      if (onShapeUpdate) onShapeUpdate(shape.id, { currentPage: newPage });
    }
  };

  const handleNextPage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedPageNum < (shape.totalPages || 1)) {
      loadedRef.current = false;
      const newPage = selectedPageNum + 1;
      setSelectedPageNum(newPage);
      if (onShapeUpdate) onShapeUpdate(shape.id, { currentPage: newPage });
    }
  };

  const handlePageThumbnailClick = (e: React.MouseEvent, pageNum: number) => {
    e.stopPropagation();
    if (pageNum !== selectedPageNum) {
      loadedRef.current = false;
      setSelectedPageNum(pageNum);
      if (onShapeUpdate) onShapeUpdate(shape.id, { currentPage: pageNum });
    }
  };

  // Toggle browser visibility
  const toggleBrowser = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowBrowser(!showBrowser);
  };

  // Auto-close browser if unselected? No, maybe keep it if user opened it? 
  // Let's keep it simple: Show button to toggle, and if selected, maybe hint it exists.

  const BROWSER_WIDTH = 200;
  const BROWSER_HEIGHT = shape.height || 800; // Match shape height? 

  return (
    <HTMLShapeBase {...props} onClick={handleClick} onDoubleClick={handleDoubleClick} isAnnotationMode={isAnnotationMode}>
      {/* Annotation Toolbar */}
      {isAnnotationMode && isSelected && toolbarPosition && (
        <AnnotationToolbar
          currentTool={annotation.state.currentTool}
          currentColor={annotation.state.currentColor}
          currentStrokeWidth={annotation.state.currentStrokeWidth}
          currentOpacity={annotation.state.currentOpacity}
          currentUnderlineStyle={annotation.state.currentUnderlineStyle}
          onToolChange={annotation.setTool}
          onColorChange={annotation.setColor}
          onStrokeWidthChange={annotation.setStrokeWidth}
          onOpacityChange={annotation.setOpacity}
          onUnderlineStyleChange={annotation.setUnderlineStyle}
          onClearAll={annotation.clearAnnotations}
          onClose={handleCloseAnnotation}
          stageScale={stageScale}
          position={toolbarPosition}
        />
      )}

      <div
        ref={contentRef}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#FFFFFF',
          border: `${borderWidth}px solid ${shape.stroke || '#D1D5DB'}`,
          borderRadius: `${4 * stageScale}px`,
          boxSizing: 'border-box',
          position: 'relative',
          pointerEvents: isAnnotationMode ? 'auto' : 'none',
        }}
      >
        {/* Main PDF Content */}
        <div style={{
          width: '100%', height: '100%', overflow: 'hidden', position: 'relative', pointerEvents: isAnnotationMode ? 'auto' : 'auto'
        }}>
          {isLoading ? (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              fontSize: '14px',
            }}>
              {t.common.loading}
            </div>
          ) : pdfImage ? (
            <img
              src={pdfImage}
              alt={`PDF Page ${selectedPageNum}`}
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                pointerEvents: 'none',
              }}
            />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ef4444',
              fontSize: '14px',
            }}>
              {t.common.error}
            </div>
          )}

          {/* Page Navigation Overlay */}
          {!isLoading && shape.totalPages && shape.totalPages > 1 && !shape.lockPageNavigation && (
            <div
              style={{
                position: 'absolute',
                bottom: 10,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                background: 'rgba(255, 255, 255, 0.95)',
                padding: '8px 12px',
                borderRadius: '20px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                fontSize: '12px',
                zIndex: 10,
                pointerEvents: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={toggleBrowser}
                style={{
                  padding: '4px 8px',
                  border: 'none',
                  background: showBrowser ? '#3b82f6' : '#e5e7eb',
                  color: showBrowser ? 'white' : '#374151',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title={t.pdf.pageList}
              >
                <span>☰</span>
              </button>

              <button
                onClick={handlePrevPage}
                disabled={selectedPageNum <= 1}
                style={{
                  padding: '4px 8px',
                  border: 'none',
                  background: selectedPageNum <= 1 ? '#e5e7eb' : '#3b82f6',
                  color: selectedPageNum <= 1 ? '#9ca3af' : 'white',
                  borderRadius: '4px',
                  cursor: selectedPageNum <= 1 ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                }}
              >
                ◀
              </button>

              <span style={{ color: '#374151', fontWeight: 500 }}>
                {selectedPageNum} / {shape.totalPages}
              </span>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onExtractPage) {
                    onExtractPage(shape.id, selectedPageNum);
                  }
                }}
                style={{
                  padding: '4px 8px',
                  border: 'none',
                  background: '#10b981',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  marginLeft: '8px',
                }}
                title={t.pdf.extractPage}
              >
                {t.pdf.extract}
              </button>

              <button
                onClick={handleNextPage}
                disabled={selectedPageNum >= (shape.totalPages || 1)}
                style={{
                  padding: '4px 8px',
                  border: 'none',
                  background: selectedPageNum >= (shape.totalPages || 1) ? '#e5e7eb' : '#3b82f6',
                  color: selectedPageNum >= (shape.totalPages || 1) ? '#9ca3af' : 'white',
                  borderRadius: '4px',
                  cursor: selectedPageNum >= (shape.totalPages || 1) ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                }}
              >
                ▶
              </button>
            </div>
          )}

          {/* Annotation Canvas (항상 표시, 편집은 annotation 모드에서만) */}
          {canvasWidth > 0 && canvasHeight > 0 && (
            <AnnotationCanvas
              width={canvasWidth}
              height={canvasHeight}
              annotations={annotation.state.annotations}
              isEditing={isAnnotationMode}
              currentTool={annotation.state.currentTool}
              currentColor={annotation.state.currentColor}
              currentStrokeWidth={annotation.state.currentStrokeWidth}
              currentOpacity={annotation.state.currentOpacity}
              currentUnderlineStyle={annotation.state.currentUnderlineStyle}
              onHighlighterAdd={handleHighlighterAdd}
              onUnderlineAdd={handleUnderlineAdd}
              onAnnotationDelete={handleAnnotationDelete}
              stageScale={stageScale}
            />
          )}
        </div>

        {/* Sidebar Browser */}
        {showBrowser && (shape.pdfFile || shape._pdfBase64) && (
          <div
            style={{
              position: 'absolute',
              top: 0,

              // Wait, HTMLShapeBase children are scaled? 
              // Usually HTMLShapeBase sets width/height. 
              // If we position absolute -200px, it will be scaled by the parent transform.
              // So we just need standard px units relative to the shape coordinate system.
              left: -220,
              width: 210,
              height: '100%',
              backgroundColor: '#f9fafb',
              borderRight: '1px solid #e5e7eb',
              borderRadius: '4px 0 0 4px',
              display: 'flex',
              flexDirection: 'column',
              pointerEvents: 'auto',
              overflow: 'hidden',
              zIndex: 5,
              boxShadow: '-2px 0 10px rgba(0,0,0,0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
          >
            {/* 헤더: 탭 + 닫기 */}
            <div style={{
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              fontSize: '12px',
            }}>
              {/* 탭 */}
              <button
                onClick={() => setBrowserTab('pages')}
                style={{
                  flex: 1, padding: '8px 0', border: 'none', cursor: 'pointer',
                  background: browserTab === 'pages' ? '#fff' : '#f3f4f6',
                  color: browserTab === 'pages' ? '#3b82f6' : '#6b7280',
                  fontWeight: browserTab === 'pages' ? 600 : 400,
                  borderBottom: browserTab === 'pages' ? '2px solid #3b82f6' : '2px solid transparent',
                }}
              >
                {t.pdf.pages}
              </button>
              {outlineItems.length > 0 && (
                <button
                  onClick={() => setBrowserTab('outline')}
                  style={{
                    flex: 1, padding: '8px 0', border: 'none', cursor: 'pointer',
                    background: browserTab === 'outline' ? '#fff' : '#f3f4f6',
                    color: browserTab === 'outline' ? '#3b82f6' : '#6b7280',
                    fontWeight: browserTab === 'outline' ? 600 : 400,
                    borderBottom: browserTab === 'outline' ? '2px solid #3b82f6' : '2px solid transparent',
                  }}
                >
                  {t.pdf.outline}
                </button>
              )}
              <button
                onClick={() => setShowBrowser(false)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', color: '#6b7280', padding: '8px' }}
              >
                ✕
              </button>
            </div>

            {/* 목차 탭 */}
            {browserTab === 'outline' && (
              <div style={{
                flex: 1, overflowY: 'auto', padding: '8px',
              }}>
                {outlineItems.map((item, idx) => (
                  <div
                    key={idx}
                    onClick={(e) => { e.stopPropagation(); handlePageThumbnailClick(e, item.pageNum); }}
                    style={{
                      padding: '5px 8px',
                      paddingLeft: `${8 + item.level * 14}px`,
                      fontSize: '12px',
                      color: selectedPageNum === item.pageNum ? '#3b82f6' : '#374151',
                      fontWeight: selectedPageNum === item.pageNum ? 600 : 400,
                      cursor: 'pointer',
                      borderRadius: '4px',
                      lineHeight: '1.4',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    onMouseEnter={(e) => { (e.target as HTMLDivElement).style.backgroundColor = '#f3f4f6'; }}
                    onMouseLeave={(e) => { (e.target as HTMLDivElement).style.backgroundColor = 'transparent'; }}
                    title={`${item.title} (p.${item.pageNum})`}
                  >
                    {item.title}
                    <span style={{ color: '#9ca3af', fontSize: '10px', marginLeft: '4px' }}>{item.pageNum}</span>
                  </div>
                ))}
              </div>
            )}

            {/* 페이지 탭 */}
            {browserTab === 'pages' && (
              <div
                ref={browserContentRef}
                style={{
                  flex: 1, overflowY: 'auto', padding: '10px',
                  display: 'flex', flexDirection: 'column', gap: '10px',
                }}
              >
                <Document file={shape.pdfFile || shape._pdfBase64} loading={<div>{t.common.loading}</div>}>
                  {Array.from(new Array(shape.totalPages || 0), (el, index) => {
                    const pageNum = index + 1;
                    const isVisible = visiblePages.has(pageNum);
                    return (
                      <div
                        key={`thumb-${pageNum}`}
                        ref={(el) => setPageRef(el, pageNum)}
                        data-page={pageNum}
                        onClick={(e) => handlePageThumbnailClick(e, pageNum)}
                        style={{
                          cursor: 'pointer',
                          border: selectedPageNum === pageNum ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          position: 'relative',
                          minHeight: '150px',
                          backgroundColor: '#fff'
                        }}
                      >
                        {isVisible ? (
                          <Page
                            pageNumber={pageNum}
                            width={180}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                          />
                        ) : (
                          <div style={{ height: '240px', background: '#f3f4f6' }} />
                        )}
                        <div style={{
                          position: 'absolute', bottom: 0, right: 0,
                          background: 'rgba(0,0,0,0.5)', color: 'white',
                          padding: '2px 6px', fontSize: '10px', borderRadius: '4px 0 0 0'
                        }}>
                          {pageNum}
                        </div>
                      </div>
                    );
                  })}
                </Document>
              </div>
            )}
          </div>
        )}

      </div>
    </HTMLShapeBase>
  );
};

export default memo(HTMLPDF);
